import React, { useState, useEffect } from 'react';
import { dashboardAPI } from '../../services/api';
import RiskMeter from './RiskMeter';
import { HelpCircle } from 'lucide-react';

/**
 * MaritimePanel Component
 * Displays MarineTraffic AIS map and Windy widget with risk meter
 */
const MaritimePanel = () => {
  const [vesselData, setVesselData] = useState(null);

  useEffect(() => {
    const loadVesselData = async () => {
      try {
        const res = await dashboardAPI.getVesselCounts();
        setVesselData(res.data);
      } catch (error) {
        console.error('Failed to load vessel counts', error);
      }
    };

    loadVesselData();
    const interval = setInterval(loadVesselData, 60 * 60 * 1000); // Refresh hourly
    return () => clearInterval(interval);
  }, []);

  const getRiskValue = () => {
    if (!vesselData) return 50;
    const deviation = Math.abs(vesselData.deviation || 0);
    // Convert deviation to risk score (0-100)
    // -20% deviation = 100 risk, 0% = 50 risk, +20% = 0 risk
    if (deviation >= 20) return 100;
    if (deviation <= 0) return 50;
    return 50 + (deviation / 20) * 50;
  };

  const getRiskTooltip = () => {
    if (!vesselData) return 'Vessel traffic data loading...';
    const riskLevel = vesselData.riskLevel;
    if (riskLevel === 'red') {
      return 'Significant decrease in vessel traffic detected. This may indicate shipping disruptions in the Strait of Hormuz, which could impact global oil trade and increase shipping costs.';
    } else if (riskLevel === 'yellow') {
      return 'Moderate decrease in vessel traffic. Monitor for potential shipping delays or route changes.';
    }
    return 'Vessel traffic is within normal range. Shipping lanes are operating normally.';
  };

  return (
    <div className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
        Maritime Traffic - Strait of Hormuz
      </h2>

      {/* Risk Meter */}
      {vesselData && (
        <div className="mb-6">
          <RiskMeter
            value={getRiskValue()}
            label="Shipping Disruption Risk"
            tooltip={getRiskTooltip()}
          />
          <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">Current Vessels: </span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {vesselData.current?.total || 0}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Tankers: </span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {vesselData.current?.tankers || 0}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">7-Day Average: </span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {vesselData.baseline7d?.avg || 0}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Deviation: </span>
              <span className={`font-semibold ${vesselData.deviation < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                {vesselData.deviation ? `${vesselData.deviation > 0 ? '+' : ''}${vesselData.deviation.toFixed(1)}%` : '0%'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* MarineTraffic AIS Map */}
      <div className="mb-6">
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
          Live Vessel Positions
          <div className="group relative ml-2">
            <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
            <div className="absolute left-0 top-6 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
              Real-time positions of ships in the Strait of Hormuz. Tankers are shown in red. Disruptions here can impact global oil prices.
            </div>
          </div>
        </div>
        <div className="w-full h-96 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
          <iframe
            src="https://www.marinetraffic.com/en/ais/embed/zoom:10/centery:26.5/centerx:56.5/maptype:4"
            title="MarineTraffic AIS Map"
            className="w-full h-full"
            loading="lazy"
            allow="geolocation"
          />
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          Showing live vessel positions in the Strait of Hormuz region
        </p>
      </div>

      {/* Windy Weather Overlay */}
      <div>
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
          Weather Conditions
          <div className="group relative ml-2">
            <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
            <div className="absolute left-0 top-6 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
              Wind and sea conditions can affect shipping operations and vessel routing decisions.
            </div>
          </div>
        </div>
        <div className="w-full h-96 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
          <iframe
            src="https://embed.windy.com/embed2.html?lat=26.5&lon=56.5&zoom=8&level=surface&overlay=wind&menu=&message=&marker=&forecast=12&calendar=now&pressure=&type=map&location=coordinates&detail=&detailLat=26.5&detailLon=56.5&metricWind=default&metricTemp=default&radarRange=-1"
            title="Windy Weather Map"
            className="w-full h-full"
            loading="lazy"
          />
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          Wind and sea conditions in the Gulf region
        </p>
      </div>
    </div>
  );
};

export default MaritimePanel;

